import { useEffect, useRef, useState } from 'react';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';
import { useAccountInfo } from 'hooks/billing/useAccountInfo';
import { useConversionPopup } from 'hooks/billing/useConversionPopup';
import { usePaidMoveFlow } from 'hooks/billing/usePaidMoveFlow';
import { BillingClient } from 'network/httpClients/BillingClient/BillingClient';
import { mixpanelService } from 'services/mixpanel';
import {
  CONVERSION_POPUP_TRIGGER_SOURCES,
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { type UsageModelBannerDismissMethod } from 'services/mixpanel/types';
import { captureBillingError } from 'services/sentry/captureBillingError';
import { ACCOUNT_TIERS } from 'types/billing';
import {
  getMigrationDaysRemaining,
  isMigratedFreeUser,
  isMigratedPaidUser,
  isMovePendingReversal,
} from 'utils/billing/conversion';
import { getTrialDiscountPct } from '../utils';

/**
 * Reads the session-dismissed flag for the migration welcome popup.
 * @returns `true` if the popup was dismissed earlier in this session; `false` on storage errors.
 */
const readSessionDismissed = (): boolean => {
  try {
    return (
      sessionStorage.getItem(
        SESSION_STORAGE_KEYS.MIGRATION_WELCOME_POPUP_DISMISSED,
      ) === 'true'
    );
  } catch {
    return false;
  }
};

/** Marks the popup as dismissed for this session. Best-effort — silently no-ops if storage is unavailable. */
const writeSessionDismissed = () => {
  try {
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.MIGRATION_WELCOME_POPUP_DISMISSED,
      'true',
    );
  } catch {
    // best-effort — popup may reappear on navigation in restricted environments
  }
};

/**
 * Controls visibility and dismissal of the migration welcome popup for migrated free users.
 * Dismissed state is per-session: the popup reappears on each new login until
 * the user selects a paid plan. Also emits the "New usage-based model" banner
 * analytics (`usage_model_banner_shown` / `usage_model_banner_dismissed`, PLT-4697).
 * @returns `isOpen` — whether the popup should render; `onSnooze(method)` — closes
 */
export const useMigrationWelcomePopup = () => {
  const { data: accountInfo } = useAccountInfo();
  const { open: openConversionPopup } = useConversionPopup();
  const [dismissed, setDismissed] = useState(readSessionDismissed);
  const shownTrackedRef = useRef(false);

  const dismiss = () => {
    setDismissed(true);
    writeSessionDismissed();
  };

  // The paid Move + its 3DS reversal recovery. A silent success dismisses the
  // popup; a card that needs auth flips `paymentActionNeeded` so we render the
  // inline consent step; `moveFailed` surfaces a non-recoverable outcome. On the
  // reversal the flow hard-reloads the page, so there's nothing to route here.
  const paidMove = usePaidMoveFlow({ onMoveComplete: dismiss });

  const isFreeCohortOpen =
    isMigratedFreeUser(accountInfo) &&
    accountInfo?.trial?.active === true &&
    accountInfo?.tier !== ACCOUNT_TIERS.paid;

  const isPaidCohortOpen =
    isMigratedPaidUser(accountInfo) &&
    (getMigrationDaysRemaining(accountInfo?.migrationEndDate) ?? 0) > 0;

  // Keep the popup open while the Move is parked awaiting reversal consent: the
  // record is momentarily `payment_action_required` (not `awaiting_move`), so
  // `isPaidCohortOpen` briefly reads false — `paymentActionNeeded` holds it open
  // through the consent step. `dismissed` still lets the user close it.
  const isOpen =
    (isFreeCohortOpen || isPaidCohortOpen || paidMove.paymentActionNeeded) &&
    !accountInfo?.trialBannerShown &&
    !dismissed;

  useEffect(() => {
    if (!isOpen || shownTrackedRef.current) return;

    shownTrackedRef.current = true;
    mixpanelService.track(MIXPANEL_EVENT_NAMES.VIEW, {
      operation: MIXPANEL_OPERATIONS.USAGE_MODEL_BANNER_SHOWN,
      interaction_details: { discount_pct: getTrialDiscountPct(accountInfo) },
    });
  }, [isOpen, accountInfo]);

  const onSnooze = (method: UsageModelBannerDismissMethod) => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.USAGE_MODEL_BANNER_DISMISSED,
      interaction_details: {
        dismiss_method: method,
        discount_pct: getTrialDiscountPct(accountInfo),
      },
    });
    paidMove.reset();
    dismiss();
  };

  const onSelectPlan = (planSku?: string) => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.SELECT_A_PLAN_CLICKED,
    });

    // Paid cohort → the server-side Move (swap on the card on file), not checkout.
    // The flow dismisses on a silent success, renders the consent step if the card
    // needs auth, or surfaces `moveFailed` on a non-recoverable outcome. Re-clicking
    // is a real retry (the BE treats a retried aborted Move as a fresh attempt).
    if (isPaidCohortOpen) {
      if (!planSku) return;

      paidMove.onMove(planSku);

      return;
    }

    // Free cohort → the existing checkout conversion popup.
    dismiss();
    BillingClient.markConversionBannerShown().catch((error) => {
      captureBillingError(error, 'mark-conversion-banner-shown');
    });
    openConversionPopup(
      undefined,
      CONVERSION_POPUP_TRIGGER_SOURCES.MIGRATION_WELCOME_POPUP,
    );
  };

  const onContactSupport = () => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.PLAN_INTRO_CONTACT_SUPPORT_CLICKED,
    });
  };

  return {
    isOpen,
    onSnooze,
    onSelectPlan,
    onContactSupport,
    isMigrating: paidMove.isBusy,
    moveFailed: paidMove.moveFailed,
    // Consent shows when the Move just parked (local) OR the account is already
    // parked on the server (a reload mid-flow) — so the user can always resume.
    showMoveConsent:
      paidMove.paymentActionNeeded || isMovePendingReversal(accountInfo),
    onConfirmReverse: paidMove.onConfirmReverse,
  };
};
